import PropTypes from 'prop-types';
import { useMemo, useState } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleQuestion } from '@fortawesome/free-solid-svg-icons';
import Skeleton from 'react-loading-skeleton';
import '../styles/companionAppUsageReport.css';

const FeatureUsageChart = ({ features = [], isLoading = false }) => {
  const [hoveredIndex, setHoveredIndex] = useState(null);

  const helpText = (
    <>
      <h6>Feature Usage</h6>
      <span className="mb-0">
        Number of active usage events (interactions, not just screen loads) per
        feature in range.
        <br />
        <br />
        <strong>Features:</strong> Dashboard, Reports, Badges Earned, My Account,
        FlashCards, Smart Stack, Study Match, Mind Mapping, Self Study, Recent
        Sessions.
        <br />
        <br />
        Y-axis = usage events; one bar per feature.
      </span>
    </>
  );

  const chartData = useMemo(
    () =>
      features.map((item) => ({
        name: item.featureName,
        value: item.usageCount ?? 0,
      })),
    [features]
  );

  const formatNumber = (value) => new Intl.NumberFormat('en-US').format(value);

  const { axisMax, ticks } = useMemo(() => {
    const maxValue = Math.max(...chartData.map((item) => item.value), 0);
    const targetTicks = 7;

    if (maxValue <= 0) {
      return {
        axisMax: 10,
        ticks: Array.from({ length: targetTicks }, (_, i) => ({
          value: Math.round((10 / (targetTicks - 1)) * (targetTicks - 1 - i)),
          topPercent: (i / (targetTicks - 1)) * 100,
        })),
      };
    }

    const rawStep = maxValue / (targetTicks - 1);
    const magnitude = 10 ** Math.floor(Math.log10(rawStep));
    const normalized = rawStep / magnitude;
    const niceFractions = [1, 2, 2.5, 5, 10];
    const niceFraction = niceFractions.find((f) => normalized <= f) ?? 10;
    const step = niceFraction * magnitude;
    const max = Math.ceil(maxValue / step) * step;
    const stepCount = Math.round(max / step);

    const tickList = Array.from({ length: stepCount + 1 }, (_, i) => {
      const index = stepCount - i;
      return {
        value: Math.round(step * index),
        topPercent: (i / stepCount) * 100,
      };
    });

    return { axisMax: max, ticks: tickList };
  }, [chartData]);

  if (isLoading) {
    const barHeights = [55, 80, 40, 95, 65, 30, 70, 50, 85, 60];

    return (
      <div className="companion-report-card page-break-avoid mt-3">
        <div className="feature-usage-chart__header">
          <Skeleton width={140} height={18} />
        </div>

        <div className="feature-usage-chart__content">
          <div className="feature-usage-chart__y-axis">
            {[0, 1, 2, 3, 4, 5, 6].map((tick) => (
              <span
                key={tick}
                className="feature-usage-chart__y-axis-label"
                style={{
                  top: `${(tick / 6) * 100}%`,
                  transform:
                    tick === 0
                      ? 'translateY(0)'
                      : tick === 6
                        ? 'translateY(-100%)'
                        : 'translateY(-50%)',
                }}
              >
                <Skeleton width={28} height={12} />
              </span>
            ))}
          </div>

          <div className="feature-usage-chart__plot">
            <div className="feature-usage-chart__bars">
              {barHeights.map((height, index) => (
                <div key={index} className="feature-usage-chart__bar-wrapper">
                  <div
                    className="feature-usage-chart__loading-bar"
                    style={{ height: `${height}%` }}
                  >
                    <Skeleton height="100%" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="feature-usage-chart__x-axis">
          <div className="feature-usage-chart__x-axis-spacer" />
          <div className="feature-usage-chart__x-axis-labels">
            {barHeights.map((_, index) => (
              <span key={index} className="feature-usage-chart__label">
                <Skeleton width={50} height={12} />
              </span>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const header = (
    <div className="feature-usage-chart__header">
      <h2 className="feature-usage-chart__title">Feature Usage</h2>
      {helpText && (
        <OverlayTrigger
          key='featureUsage'
          placement="top"
          overlay={<Tooltip id="feature-usage-chart-tooltip" className="additional-options-tooltip"> {helpText} </Tooltip>}
        >
          <FontAwesomeIcon className="circle-question mx-1" icon={faCircleQuestion} />
        </OverlayTrigger>
      )}
    </div>
  );

  if (!chartData.length) {
    return (
      <div className="companion-report-card page-break-avoid mt-3">
        {header}
        <div className="companion-empty-state feature-usage-chart__empty">
          No feature usage data available
        </div>
      </div>
    );
  }

  return (
    <div className="companion-report-card page-break-avoid mt-3">
      {header}

      <div className="feature-usage-chart__content">
        <div className="feature-usage-chart__y-axis">
          {ticks.map((tick) => (
            <span
              key={tick.value}
              className="feature-usage-chart__y-axis-label"
              style={{
                top: `${tick.topPercent}%`,
                transform:
                  tick.topPercent === 0
                    ? 'translateY(0)'
                    : tick.topPercent === 100
                      ? 'translateY(-100%)'
                      : 'translateY(-50%)',
              }}
            >
              {formatNumber(tick.value)}
            </span>
          ))}
        </div>

        <div className="feature-usage-chart__plot">
          <div className="feature-usage-chart__grid">
            {ticks.map((tick) => (
              <div
                key={tick.topPercent}
                className="feature-usage-chart__grid-line"
                style={{ top: `${tick.topPercent}%` }}
              />
            ))}
          </div>

          <div className="feature-usage-chart__bars">
            {chartData.map((item, index) => {
              const height = axisMax > 0 ? (item.value / axisMax) * 100 : 0;
              const isHovered = hoveredIndex === index;

              return (
                <div
                  key={item.name}
                  className="feature-usage-chart__bar-wrapper"
                  onMouseEnter={() => setHoveredIndex(index)}
                  onMouseLeave={() => setHoveredIndex(null)}
                >
                  {isHovered && (
                    <div
                      className="feature-usage-chart__tooltip"
                      style={{ bottom: `calc(${height}% + 12px)` }}
                    >
                      <div className="feature-usage-chart__tooltip-title">
                        {item.name}
                      </div>
                      <div className="feature-usage-chart__tooltip-row">
                        <span className="feature-usage-chart__tooltip-swatch" />
                        {formatNumber(item.value)}
                      </div>
                    </div>
                  )}

                  <div
                    className={`feature-usage-chart__bar${isHovered ? ' feature-usage-chart__bar--hovered' : ''}`}
                    style={{ height: `${height}%` }}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="feature-usage-chart__x-axis mb-2">
        <div className="feature-usage-chart__x-axis-spacer" />
        <div className="feature-usage-chart__x-axis-labels">
          {chartData.map((item) => (
            <span key={item.name} className="feature-usage-chart__label">
              {item.name}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

FeatureUsageChart.propTypes = {
  features: PropTypes.arrayOf(
    PropTypes.shape({
      featureName: PropTypes.string.isRequired,
      usageCount: PropTypes.number,
    })
  ),
  isLoading: PropTypes.bool
};

export default FeatureUsageChart;